import type { CsticUpdate } from "~/features/configurator/Linear/api/sendLinearConfiguratorUpdate";
import type { KmatType } from "~/features/configurator/Linear/kmatType";
import type { Kmat, LinearStep } from "~/features/configurator/Linear/models";

export interface ConfiguratorCsticAddress {
  instanceId: string;
  csticName: string;
}

export interface ConfiguratorCsticChange extends ConfiguratorCsticAddress {
  value: string | null;
}

export type ConfiguratorCsticTarget = Pick<CsticUpdate, "key" | "type" | "index">;

export interface ConfiguratorUpdateCommand {
  configId: string;
  currentStepNumber: number;
  navigation: boolean;
  changes: ConfiguratorCsticChange[];
}

export interface ConfiguratorResetStepCommand {
  configId: string;
  stepNumber: number;
  scope: "CURRENT_AND_FOLLOWING";
  targets: ConfiguratorCsticAddress[];
}

export interface ConfiguratorResetCommand {
  configId: string;
}

export interface ConfiguratorAddToCartCommand {
  configId: string;
  currentStepNumber: number;
  quantity: number;
  priceReference: string;
  configurationBasePrice?: number;
}

export type ConfiguratorCommandState = {
  configId: string;
  currentStep: number;
  kmats: Record<KmatType, Kmat[]>;
  steps: LinearStep[];
  quantity: number;
  priceReference: string;
};

export type ConfiguratorUpdateCommandResult =
  | {
      kind: "command";
      command: ConfiguratorUpdateCommand;
    }
  | {
      kind: "local-only";
    };

export function buildConfiguratorMutationCommand(
  state: ConfiguratorCommandState,
  updates: CsticUpdate[],
): ConfiguratorUpdateCommandResult {
  if (updates.length === 0) {
    throw new Error("A configurator mutation requires at least one CSTIC update");
  }

  const changes = deduplicateChanges(updates.flatMap((update) => resolveUpdate(state.kmats, update)));

  if (changes.length === 0) {
    return { kind: "local-only" };
  }

  return {
    kind: "command",
    command: {
      configId: state.configId,
      currentStepNumber: state.currentStep + 1,
      navigation: false,
      changes,
    },
  };
}

export function buildConfiguratorNavigationCommand(
  state: Pick<ConfiguratorCommandState, "configId" | "currentStep">,
): ConfiguratorUpdateCommand {
  return {
    configId: state.configId,
    currentStepNumber: state.currentStep + 1,
    navigation: true,
    changes: [],
  };
}

export function buildConfiguratorResetStepCommand(
  state: ConfiguratorCommandState,
  stepNumber = state.currentStep,
): ConfiguratorResetStepCommand {
  if (!Number.isInteger(stepNumber) || stepNumber < 0 || stepNumber >= state.steps.length) {
    throw new Error(`Unknown configurator step index: ${stepNumber}`);
  }

  const csticNames = new Set(state.steps.slice(stepNumber).flatMap((step) => Object.keys(step.defaultValues)));
  const targets = deduplicateAddresses(
    Object.values(state.kmats)
      .flatMap((kmats) => kmats)
      .flatMap((kmat) =>
        [...csticNames]
          .map((csticName) => kmat.cstics[csticName])
          .filter((cstic) => cstic && !cstic.fake && !cstic.readonly)
          .map((cstic) => ({
            instanceId: resolveRuntimeInstanceId(kmat),
            csticName: cstic.key,
          })),
      ),
  );
  if (targets.length === 0) {
    throw new Error("A configurator step reset requires at least one server-owned CSTIC");
  }

  return {
    configId: state.configId,
    stepNumber: stepNumber + 1,
    scope: "CURRENT_AND_FOLLOWING",
    targets,
  };
}

export function buildConfiguratorResetStepCommandForTargets(
  state: ConfiguratorCommandState,
  resetTargets: ConfiguratorCsticTarget[],
  stepNumber = state.currentStep,
): ConfiguratorResetStepCommand {
  if (resetTargets.length === 0) {
    throw new Error("A targeted configurator reset requires at least one CSTIC");
  }
  if (!Number.isInteger(stepNumber) || stepNumber < 0 || stepNumber >= state.steps.length) {
    throw new Error(`Unknown configurator step index: ${stepNumber}`);
  }

  const targets = deduplicateAddresses(resetTargets.flatMap((target) => resolveTargetAddresses(state.kmats, target)));
  if (targets.length === 0) {
    throw new Error("A targeted configurator reset requires at least one server-owned CSTIC");
  }

  return {
    configId: state.configId,
    stepNumber: stepNumber + 1,
    scope: "CURRENT_AND_FOLLOWING",
    targets,
  };
}

export function buildConfiguratorResetCommand(
  state: Pick<ConfiguratorCommandState, "configId">,
): ConfiguratorResetCommand {
  return { configId: state.configId };
}

export function buildConfiguratorAddToCartCommand(
  state: Pick<ConfiguratorCommandState, "configId" | "currentStep" | "quantity" | "priceReference">,
  priceReference = state.priceReference,
  configurationBasePrice?: number,
): ConfiguratorAddToCartCommand {
  if (
    configurationBasePrice !== undefined &&
    (!Number.isFinite(configurationBasePrice) || configurationBasePrice < 0)
  ) {
    throw new Error("Configurator base price must be a finite non-negative number");
  }
  return {
    configId: state.configId,
    currentStepNumber: state.currentStep + 1,
    quantity: state.quantity,
    priceReference,
    ...(configurationBasePrice === undefined ? {} : { configurationBasePrice }),
  };
}

function resolveUpdate(kmatsByType: Record<KmatType, Kmat[]>, update: CsticUpdate): ConfiguratorCsticChange[] {
  return resolveTargetAddresses(kmatsByType, update).map((address) => ({
    ...address,
    value: update.value,
  }));
}

function resolveTargetAddresses(
  kmatsByType: Record<KmatType, Kmat[]>,
  target: ConfiguratorCsticTarget,
): ConfiguratorCsticAddress[] {
  const kmats = kmatsByType[target.type];
  if (!kmats?.length) {
    throw new Error(`Unknown configurator KMAT type: ${target.type}`);
  }

  const targetKmats = target.index === undefined ? kmats : [requireKmatAtIndex(kmats, target.type, target.index)];
  const matchingCstics = targetKmats
    .map((kmat) => ({ kmat, cstic: kmat.cstics[target.key] }))
    .filter(({ cstic }) => cstic !== undefined);

  if (matchingCstics.length === 0) {
    const indexDescription = target.index === undefined ? "all instances" : `instance index ${target.index}`;
    throw new Error(`Unknown CSTIC ${target.key} for ${target.type} ${indexDescription}`);
  }

  return matchingCstics
    .filter(({ cstic }) => !cstic.fake && !cstic.readonly)
    .map(({ kmat, cstic }) => ({
      instanceId: resolveRuntimeInstanceId(kmat),
      csticName: cstic.key,
    }));
}

function resolveRuntimeInstanceId(kmat: Kmat): string {
  // The v1 response keeps SAP UI-group IDs in Kmat.id. Sub-KMAT parentId is
  // the INSTANCE group while a root Kmat.id is its top CSTIC group. Both use
  // the established `${instanceId}-...` SAP group-ID format.
  const groupId = kmat.parentId ?? kmat.id;
  const separator = groupId.indexOf("-");
  if (separator < 1) {
    throw new Error(`Invalid configurator instance group ID: ${groupId}`);
  }
  return groupId.substring(0, separator);
}

function requireKmatAtIndex(kmats: Kmat[], type: KmatType, index: number): Kmat {
  if (!Number.isInteger(index) || index < 0 || index >= kmats.length) {
    throw new Error(`Unknown ${type} KMAT index: ${index}`);
  }
  return kmats[index];
}

function deduplicateChanges(changes: ConfiguratorCsticChange[]): ConfiguratorCsticChange[] {
  const changesByAddress = new Map<string, ConfiguratorCsticChange>();

  for (const change of changes) {
    const key = addressKey(change);
    const existing = changesByAddress.get(key);
    if (existing && existing.value !== change.value) {
      throw new Error(`Contradictory updates for ${change.instanceId}/${change.csticName}`);
    }
    changesByAddress.set(key, change);
  }

  return [...changesByAddress.values()];
}

function deduplicateAddresses(addresses: ConfiguratorCsticAddress[]): ConfiguratorCsticAddress[] {
  return [...new Map(addresses.map((address) => [addressKey(address), address])).values()];
}

function addressKey(address: ConfiguratorCsticAddress): string {
  return `${address.instanceId}\u0000${address.csticName}`;
}
